/**
 * useFeed - Hook for fetching real feed posts from Supabase
 *
 * Bug #16/25 Fix: Feed now uses real posts from database instead of ghost data
 *
 * Features:
 * - Fetches posts from database with user info
 * - Falls back to ghost data if no real posts exist
 * - Supports pagination (offset/limit)
 * - Real-time updates via Supabase subscription
 * - Includes user's liked status for each post
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// Cast to any to bypass strict typing - tables exist in DB but not in generated types
const db = supabase as any;

export interface FeedPost {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  avatar?: string | null;
  content: string | null;
  caption?: string;
  media_url: string | null;
  videoUrl?: string | null;
  media_type: string | null;
  thumbnail_url: string | null;
  thumbnail?: string | null;
  duration: number | null;
  like_count: number;
  likes?: number;
  comment_count: number;
  comments?: number;
  share_count: number;
  shares?: number;
  created_at: string;
  is_liked: boolean;
  // For ghost data compatibility
  gradient?: string;
  music?: string;
}

interface UseFeedOptions {
  /** Number of posts to fetch per page */
  limit?: number;
  /** Enable real-time updates */
  realtime?: boolean;
  /** Ghost data fallback when no real posts exist */
  ghostFallback?: FeedPost[];
}

interface UseFeedReturn {
  /** Array of feed posts */
  feed: FeedPost[];
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Whether there are more posts to load */
  hasMore: boolean;
  /** Load more posts (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh the feed */
  refresh: () => Promise<void>;
  /** Whether we're using ghost data fallback */
  isGhostData: boolean;
}

export function useFeed(options: UseFeedOptions = {}): UseFeedReturn {
  const { limit = 20, realtime = true, ghostFallback = [] } = options;

  const [feed, setFeed] = useState<FeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isGhostData, setIsGhostData] = useState(false);
  const [offset, setOffset] = useState(0);

  // Track if we've done initial load
  const initialLoadDone = useRef(false);

  // Fetch posts from database
  const fetchPosts = useCallback(async (currentOffset: number, append: boolean = false) => {
    try {
      // Try to use the get_feed_posts RPC function
      const { data, error: rpcError } = await db.rpc('get_feed_posts', {
        p_limit: limit,
        p_offset: currentOffset,
      });

      if (rpcError) {
        // If RPC fails, try direct query
        console.warn('[useFeed] RPC failed, trying direct query:', rpcError.message);

        const { data: directData, error: directError } = await db
          .from('posts')
          .select(`
            id,
            user_id,
            content,
            media_url,
            media_type,
            thumbnail_url,
            duration,
            like_count,
            comment_count,
            share_count,
            created_at,
            visibility,
            profiles!posts_user_id_fkey (
              username,
              avatar_url
            )
          `)
          .eq('visibility', 'public')
          .order('created_at', { ascending: false })
          .range(currentOffset, currentOffset + limit - 1);

        if (directError) {
          throw directError;
        }

        // Transform to match expected format
        const transformedData = (directData || []).map((post: any) => ({
          id: post.id,
          user_id: post.user_id,
          username: post.profiles?.username || 'Anonymous',
          avatar_url: post.profiles?.avatar_url || null,
          avatar: post.profiles?.avatar_url || null,
          content: post.content,
          caption: post.content,
          media_url: post.media_url,
          videoUrl: post.media_type === 'video' ? post.media_url : null,
          media_type: post.media_type,
          thumbnail_url: post.thumbnail_url,
          thumbnail: post.thumbnail_url,
          duration: post.duration,
          like_count: post.like_count || 0,
          likes: post.like_count || 0,
          comment_count: post.comment_count || 0,
          comments: post.comment_count || 0,
          share_count: post.share_count || 0,
          shares: post.share_count || 0,
          created_at: post.created_at,
          is_liked: false, // Will be updated separately
        }));

        return transformedData;
      }

      // RPC succeeded - transform to include FeedView-compatible fields
      const transformedData = (data || []).map((post: any) => ({
        ...post,
        avatar: post.avatar_url,
        caption: post.content,
        videoUrl: post.media_type === 'video' ? post.media_url : null,
        thumbnail: post.thumbnail_url,
        likes: post.like_count || 0,
        comments: post.comment_count || 0,
        shares: post.share_count || 0,
      }));

      return transformedData;
    } catch (err) {
      console.error('[useFeed] Fetch error:', err);
      throw err;
    }
  }, [limit]);

  // Initial load
  useEffect(() => {
    if (initialLoadDone.current) return;

    const loadInitialFeed = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const posts = await fetchPosts(0, false);

        if (posts.length === 0 && ghostFallback.length > 0) {
          // No real posts - use ghost data
          console.log('[useFeed] No real posts, using ghost data fallback');
          setFeed(ghostFallback);
          setIsGhostData(true);
          setHasMore(false);
        } else {
          setFeed(posts);
          setIsGhostData(false);
          setHasMore(posts.length >= limit);
          setOffset(posts.length);
        }

        initialLoadDone.current = true;
      } catch (err) {
        console.error('[useFeed] Initial load failed:', err);
        setError(err instanceof Error ? err : new Error('Failed to load feed'));

        // Fall back to ghost data on error
        if (ghostFallback.length > 0) {
          setFeed(ghostFallback);
          setIsGhostData(true);
          setHasMore(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialFeed();
  }, [fetchPosts, limit, ghostFallback]);

  // Real-time subscription for new posts
  useEffect(() => {
    if (!realtime || isGhostData) return;

    const channel = supabase
      .channel('posts-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'posts',
          filter: 'visibility=eq.public',
        },
        async (payload) => {
          console.log('[useFeed] New post received:', payload.new.id);

          // Fetch the full post with user info
          const { data: newPost } = await db
            .from('posts')
            .select(`
              id,
              user_id,
              content,
              media_url,
              media_type,
              thumbnail_url,
              duration,
              like_count,
              comment_count,
              share_count,
              created_at,
              profiles!posts_user_id_fkey (
                username,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (newPost) {
            const transformedPost: FeedPost = {
              id: newPost.id,
              user_id: newPost.user_id,
              username: (newPost.profiles as any)?.username || 'Anonymous',
              avatar_url: (newPost.profiles as any)?.avatar_url || null,
              avatar: (newPost.profiles as any)?.avatar_url || null,
              content: newPost.content,
              caption: newPost.content,
              media_url: newPost.media_url,
              videoUrl: newPost.media_type === 'video' ? newPost.media_url : null,
              media_type: newPost.media_type,
              thumbnail_url: newPost.thumbnail_url,
              thumbnail: newPost.thumbnail_url,
              duration: newPost.duration,
              like_count: newPost.like_count || 0,
              likes: newPost.like_count || 0,
              comment_count: newPost.comment_count || 0,
              comments: newPost.comment_count || 0,
              share_count: newPost.share_count || 0,
              shares: newPost.share_count || 0,
              created_at: newPost.created_at,
              is_liked: false,
            };

            setFeed((prev) => [transformedPost, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [realtime, isGhostData]);

  // Load more posts (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || isGhostData) return;

    setIsLoading(true);

    try {
      const posts = await fetchPosts(offset, true);

      if (posts.length === 0) {
        setHasMore(false);
      } else {
        setFeed((prev) => [...prev, ...posts]);
        setOffset((prev) => prev + posts.length);
        setHasMore(posts.length >= limit);
      }
    } catch (err) {
      console.error('[useFeed] Load more failed:', err);
      setError(err instanceof Error ? err : new Error('Failed to load more posts'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchPosts, offset, hasMore, isLoading, isGhostData, limit]);

  // Refresh the feed
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setOffset(0);

    try {
      const posts = await fetchPosts(0, false);

      if (posts.length === 0 && ghostFallback.length > 0) {
        setFeed(ghostFallback);
        setIsGhostData(true);
        setHasMore(false);
      } else {
        setFeed(posts);
        setIsGhostData(false);
        setHasMore(posts.length >= limit);
        setOffset(posts.length);
      }
    } catch (err) {
      console.error('[useFeed] Refresh failed:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh feed'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchPosts, limit, ghostFallback]);

  return {
    feed,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
    isGhostData,
  };
}

export default useFeed;
